/**
 * Weddings list + current selection.
 * Kept separate from the per-domain CRUD hooks because it drives the "which
 * wedding is active" state that every other domain listens to.
 */
import { useCallback, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useWeddingCrud() {
  const { user } = useAuth();
  const [weddings, setWeddings] = useState([]);
  const [currentWedding, setCurrentWedding] = useState(null);
  // Start in the loading state so consumers (e.g. <OnboardingGate>) don't
  // mistake "haven't fetched yet" for "user has zero weddings" and trigger
  // a wrong redirect on first render.
  const [loading, setLoading] = useState(true);
  const [initialised, setInitialised] = useState(false);

  const fetchWeddings = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/weddings`, { withCredentials: true });
      setWeddings(data);
      setCurrentWedding((prev) => prev || data[0] || null);
    } catch (err) {
      console.error('Error fetching weddings:', err);
    } finally {
      setLoading(false);
      setInitialised(true);
    }
  }, [user]);

  const createWedding = useCallback(async (weddingData) => {
    const { data } = await axios.post(`${API}/weddings`, weddingData, { withCredentials: true });
    setWeddings((prev) => [...prev, data]);
    setCurrentWedding(data);
    return data;
  }, []);

  const updateWedding = useCallback(async (weddingId, weddingData) => {
    const { data } = await axios.put(`${API}/weddings/${weddingId}`, weddingData, { withCredentials: true });
    setWeddings((prev) => prev.map((w) => (w.id === weddingId ? data : w)));
    setCurrentWedding((prev) => (prev?.id === weddingId ? data : prev));
    return data;
  }, []);

  const deleteWedding = useCallback(async (weddingId) => {
    await axios.delete(`${API}/weddings/${weddingId}`, { withCredentials: true });
    setWeddings((prev) => {
      const next = prev.filter((w) => w.id !== weddingId);
      setCurrentWedding((current) =>
        current?.id === weddingId ? (next[0] || null) : current
      );
      return next;
    });
  }, []);

  return {
    weddings, currentWedding, loading, initialised,
    setCurrentWedding,
    fetchWeddings, createWedding, updateWedding, deleteWedding,
  };
}
